////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

// I. FILE HEADER [WHITE #FFFFFF]
// ⚪ Homepage Pea for Taytrom Publishing Co - Pure presentation component

// III. IMPORTS [GREEN #00FF00]
// 🟢 Third-party dependencies
import { FC, useState, useEffect } from 'react';
import styles from './TaytromPublishingCo_Kings_Home_SRC_Homepage_Lobby_Pea.module.css';

// IV. INDEPENDENT CONSTANTS [TEAL #00FFFF]
// 🔵 Component props interface
interface Book {
  id: string;
  title: string;
  client: string;
  description: string;
  pageCount: number;
  publishDate: string;
}

interface ServiceOffering {
  id: string;
  title: string;
  description: string;
  icon: string;
}

interface CompanyStatistic {
  id: string;
  label: string;
  value: string;
  description: string;
  icon: string;
}

interface ProcessStep {
  id: string;
  stepNumber: number;
  title: string;
  description: string;
  duration: string;
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
}

interface AiEraImperativePoint {
  id: string;
  icon: string;
  stat: string;
  label: string;
  description: string;
}

interface FoundingClientBenefit {
  id: string;
  icon: string;
  title: string;
  description: string;
}

interface FoundingClientOffer {
  title: string;
  subtitle: string;
  description: string;
  spotsRemaining: number;
  totalSpots: number;
  benefits: FoundingClientBenefit[];
}

interface TirdvtoStep {
  id: string;
  phase: string;
  phaseShort: string;
  color: string;
  icon: string;
  description: string;
}

interface TirdvtoExample {
  id: string;
  domain: string;
  scenario: string;
  trigger: string;
  input: string;
  requirements: {
    definitions: string;
    validations: string;
    transformations: string;
  };
  output: string;
}

interface TirdvtoFramework {
  title: string;
  subtitle: string;
  description: string;
  tagline: string;
  steps: TirdvtoStep[];
  examples: TirdvtoExample[];
}

interface PeaHomepageProps {
  books: Book[];
  services: ServiceOffering[];
  statistics: CompanyStatistic[];
  processSteps: ProcessStep[];
  aiEraImperative: AiEraImperativePoint[];
  tirdvtoFramework: TirdvtoFramework;
  faqs: FAQ[];
  foundingClientOffer: FoundingClientOffer;
  selectedBookId: string | null;
  isLoading: boolean;
  onBookSelect: (bookId: string) => void;
  onAiInterviewStart: () => void;
  onBookExplore: () => void;
  className?: string;
}

// V. FUNCTION/CLASS NAME [BLUE #0000FF] - OUTPUT DECLARATION
// 🔵 Homepage Pea Component
const Pea_Homepage: FC<PeaHomepageProps> = ({
  books,
  services,
  statistics,
  processSteps,
  aiEraImperative,
  tirdvtoFramework,
  faqs,
  foundingClientOffer,
  selectedBookId,
  isLoading,
  onBookSelect,
  onAiInterviewStart,
  onBookExplore,
  className
}) => {
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Scroll detection for navigation enhancement
  const [isScrolled, setIsScrolled] = useState(false);

  // Sections dropdown state
  const [isSectionsDropdownOpen, setIsSectionsDropdownOpen] = useState(false);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  const handleSectionsDropdownToggle = () => {
    setIsSectionsDropdownOpen(!isSectionsDropdownOpen);
  };

  const handleSectionsDropdownClose = () => {
    setIsSectionsDropdownOpen(false);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      handleSectionsDropdownClose();
    }
  };

  // FAQ accordion state
  const [openFaqId, setOpenFaqId] = useState<string | null>(null);

  // Scroll listener for navigation enhancement
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // X. RENDER/EXPORTS [BLACK #000000] - OUTPUT DELIVERY
  // Show loading state while data initializes
  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <div className={styles.loadingText}>Loading Taytrom Publishing Co...</div>
      </div>
    );
  }

  return (
    <div className={`${styles.homepageContainer} ${className || ''}`}>
      {/* Skip Link for Accessibility */}
      <a href="#main-content" className={styles.skipLink}>
        Skip to main content
      </a>

      {/* Navigation Header */}
      <header className={`${styles.navigationHeader} ${isScrolled ? styles.scrolled : ''}`} role="banner">
        <div className={styles.navigationContent}>
          <a href="/" className={styles.navigationBrand} aria-label="Taytrom Publishing Co. Home">
            <svg width="28" height="28" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '10px', display: 'inline-block', verticalAlign: 'middle' }}>
              <defs>
                <linearGradient id="navBulbGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{stopColor:'#6a1b9a',stopOpacity:1}} />
                  <stop offset="30%" style={{stopColor:'#7b1fa2',stopOpacity:1}} />
                  <stop offset="60%" style={{stopColor:'#9c27b0',stopOpacity:1}} />
                  <stop offset="85%" style={{stopColor:'#ba68c8',stopOpacity:0.95}} />
                  <stop offset="100%" style={{stopColor:'#ffd700',stopOpacity:0.9}} />
                </linearGradient>
                <radialGradient id="navGlowGradient" cx="50%" cy="40%">
                  <stop offset="0%" style={{stopColor:'#ffd700',stopOpacity:0.8}} />
                  <stop offset="25%" style={{stopColor:'#ffb300',stopOpacity:0.5}} />
                  <stop offset="50%" style={{stopColor:'#9c27b0',stopOpacity:0.3}} />
                  <stop offset="100%" style={{stopColor:'#6a1b9a',stopOpacity:0}} />
                </radialGradient>
                <linearGradient id="navFilamentGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{stopColor:'#fff9e6',stopOpacity:1}} />
                  <stop offset="20%" style={{stopColor:'#ffd700',stopOpacity:1}} />
                  <stop offset="100%" style={{stopColor:'#ffb300',stopOpacity:1}} />
                </linearGradient>
                <linearGradient id="navBaseGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{stopColor:'#cfd8dc',stopOpacity:1}} />
                  <stop offset="30%" style={{stopColor:'#b0bec5',stopOpacity:1}} />
                  <stop offset="70%" style={{stopColor:'#90a4ae',stopOpacity:1}} />
                  <stop offset="100%" style={{stopColor:'#78909c',stopOpacity:1}} />
                </linearGradient>
              </defs>
              <ellipse cx="100" cy="80" rx="75" ry="85" fill="url(#navGlowGradient)" opacity="0.3"/>
              <path d="M 100 20 C 130 20, 150 40, 150 70 C 150 90, 140 105, 130 120 C 125 130, 120 138, 120 145 L 120 155 L 80 155 L 80 145 C 80 138, 75 130, 70 120 C 60 105, 50 90, 50 70 C 50 40, 70 20, 100 20 Z" fill="url(#navBulbGradient)" stroke="rgba(255,255,255,0.4)" strokeWidth="2.5"/>
              <ellipse cx="82" cy="48" rx="22" ry="28" fill="rgba(255,255,255,0.3)" opacity="0.6"/>
              <circle cx="100" cy="65" r="3" fill="#fff9e6" opacity="0.9"/>
              <line x1="100" y1="69" x2="100" y2="85" stroke="url(#navFilamentGradient)" strokeWidth="3" strokeLinecap="round"/>
              <circle cx="90" cy="95" r="3.5" fill="url(#navFilamentGradient)"/>
              <circle cx="100" cy="95" r="3.5" fill="url(#navFilamentGradient)"/>
              <circle cx="110" cy="95" r="3.5" fill="url(#navFilamentGradient)"/>
              <line x1="100" y1="98" x2="100" y2="115" stroke="url(#navFilamentGradient)" strokeWidth="3" strokeLinecap="round"/>
              <circle cx="100" cy="120" r="3" fill="#fff9e6" opacity="0.9"/>
              <rect x="80" y="155" width="40" height="8" rx="2" fill="url(#navBaseGradient)"/>
              <rect x="80" y="165" width="40" height="6" rx="2" fill="url(#navBaseGradient)" opacity="0.85"/>
              <rect x="80" y="173" width="40" height="6" rx="2" fill="url(#navBaseGradient)" opacity="0.7"/>
            </svg>
            Taytrom Publishing Co.
          </a>
          <nav className={styles.navigationLinks} role="navigation" aria-label="Main navigation">
            <a href="/" className={`${styles.navigationLink} ${styles.active}`} aria-current="page">
              Home
            </a>
            <div className={styles.navigationDropdown}>
              <button
                className={`${styles.navigationLink} ${styles.dropdownToggle}`}
                onClick={handleSectionsDropdownToggle}
                aria-expanded={isSectionsDropdownOpen}
                aria-haspopup="true"
                aria-label="Jump to section"
              >
                Sections {isSectionsDropdownOpen ? '▲' : '▼'}
              </button>
              {isSectionsDropdownOpen && (
                <div className={styles.dropdownMenu} role="menu">
                  <button className={styles.dropdownItem} role="menuitem" onClick={() => scrollToSection('ai-era-section')}>
                    AI Era Imperative
                  </button>
                  <button className={styles.dropdownItem} role="menuitem" onClick={() => scrollToSection('tirdvto-section')}>
                    TIR{'{DVT}'}O Framework
                  </button>
                  <button className={styles.dropdownItem} role="menuitem" onClick={() => scrollToSection('services-section')}>
                    Services
                  </button>
                  <button className={styles.dropdownItem} role="menuitem" onClick={() => scrollToSection('books-section')}>
                    Featured Books
                  </button>
                  <button className={styles.dropdownItem} role="menuitem" onClick={() => scrollToSection('statistics-section')}>
                    Statistics
                  </button>
                  <button className={styles.dropdownItem} role="menuitem" onClick={() => scrollToSection('founding-client-section')}>
                    Founding Client Program
                  </button>
                  <button className={styles.dropdownItem} role="menuitem" onClick={() => scrollToSection('process-section')}>
                    Process
                  </button>
                  <button className={styles.dropdownItem} role="menuitem" onClick={() => scrollToSection('pricing-section')}>
                    Pricing
                  </button>
                  <button className={styles.dropdownItem} role="menuitem" onClick={() => scrollToSection('faq-section')}>
                    FAQs
                  </button>
                </div>
              )}
            </div>
            <a href="/library" className={styles.navigationLink} onClick={(e) => { e.preventDefault(); onBookExplore(); }} aria-label="Browse our library of published books">
              Library
            </a>
            <a href="/ai-interviewer" className={styles.navigationLink} aria-label="Start the AI Socratic Interviewer">
              AI Interviewer
            </a>
            <a href="/about" className={styles.navigationLink} aria-label="Learn about Taytrom Publishing Co.">
              About
            </a>
            <a href="/contact" className={styles.navigationLink} aria-label="Contact us">
              Contact
            </a>
          </nav>
{/* START institutional-pass */}
          <div className={styles.navigationActions}>
            <button
              onClick={onAiInterviewStart}
              className={styles.navigationCtaButton}
              aria-label="Initiate Author Interview - Begin the publication process"
            >
              Initiate Author Interview
            </button>
          </div>
{/* END institutional-pass */}
          <button
            className={styles.mobileMenuButton}
            onClick={handleMobileMenuToggle}
            aria-label={isMobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation"
          >
            {isMobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>
      </header>

      {/* Mobile Navigation Menu */}
      <div
        id="mobile-navigation"
        className={`${styles.mobileNavigationMenu} ${isMobileMenuOpen ? styles.open : ''}`}
        role="navigation"
        aria-label="Mobile navigation"
        aria-hidden={!isMobileMenuOpen}
      >
        <nav className={styles.mobileNavigationLinks}>
          <a href="/" className={`${styles.mobileNavigationLink} ${styles.active}`} onClick={handleMobileMenuClose} aria-current="page">
            Home
          </a>
          <a href="/library" className={styles.mobileNavigationLink} onClick={(e) => { e.preventDefault(); onBookExplore(); handleMobileMenuClose(); }} aria-label="Browse our library">
            Library
          </a>
          <a href="/ai-interviewer" className={styles.mobileNavigationLink} onClick={handleMobileMenuClose} aria-label="Start AI Interviewer">
            AI Interviewer
          </a>
          <a href="/about" className={styles.mobileNavigationLink} onClick={handleMobileMenuClose} aria-label="About us">
            About
          </a>
          <a href="/contact" className={styles.mobileNavigationLink} onClick={handleMobileMenuClose} aria-label="Contact us">
            Contact
          </a>
        </nav>
      </div>

      {/* Hero Section */}
      <section id="main-content" className={styles.heroSection} role="main" aria-labelledby="hero-title">
        <div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
            <svg width="600" height="180" viewBox="0 0 800 240" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ maxWidth: '100%', height: 'auto' }}>
              <defs>
                <linearGradient id="heroTextGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style={{stopColor:'#6a1b9a',stopOpacity:1}} />
                  <stop offset="35%" style={{stopColor:'#7b1fa2',stopOpacity:1}} />
                  <stop offset="65%" style={{stopColor:'#9c27b0',stopOpacity:1}} />
                  <stop offset="100%" style={{stopColor:'#ffd700',stopOpacity:1}} />
                </linearGradient>
                <linearGradient id="heroAccentGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style={{stopColor:'#ffb300',stopOpacity:1}} />
                  <stop offset="50%" style={{stopColor:'#ffd700',stopOpacity:1}} />
                  <stop offset="100%" style={{stopColor:'#ffb300',stopOpacity:1}} />
                </linearGradient>
                <linearGradient id="heroBulbGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{stopColor:'#6a1b9a',stopOpacity:1}} />
                  <stop offset="60%" style={{stopColor:'#9c27b0',stopOpacity:1}} />
                  <stop offset="100%" style={{stopColor:'#ffd700',stopOpacity:0.9}} />
                </linearGradient>
                <radialGradient id="heroGlowCompact" cx="50%" cy="40%">
                  <stop offset="0%" style={{stopColor:'#ffd700',stopOpacity:0.6}} />
                  <stop offset="100%" style={{stopColor:'#9c27b0',stopOpacity:0}} />
                </radialGradient>
                <linearGradient id="heroFilamentCompact" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{stopColor:'#fff9e6',stopOpacity:1}} />
                  <stop offset="100%" style={{stopColor:'#ffd700',stopOpacity:1}} />
                </linearGradient>
              </defs>
              <ellipse cx="100" cy="120" rx="80" ry="90" fill="url(#heroGlowCompact)" opacity="0.3"/>
              <g transform="translate(20, 40)">
                <ellipse cx="80" cy="80" rx="50" ry="55" fill="url(#heroGlowCompact)" opacity="0.4"/>
                <path d="M 80 30 C 100 30, 115 42, 115 60 C 115 75, 108 85, 102 95 C 99 100, 97 105, 97 110 L 97 118 L 63 118 L 63 110 C 63 105, 61 100, 58 95 C 52 85, 45 75, 45 60 C 45 42, 60 30, 80 30 Z" fill="url(#heroBulbGradient)" stroke="rgba(255,255,255,0.3)" strokeWidth="2"/>
                <ellipse cx="70" cy="50" rx="15" ry="18" fill="rgba(255,255,255,0.3)" opacity="0.6"/>
                <circle cx="80" cy="65" r="3" fill="url(#heroFilamentCompact)"/>
                <line x1="80" y1="68" x2="80" y2="80" stroke="url(#heroFilamentCompact)" strokeWidth="2"/>
                <circle cx="75" cy="85" r="2" fill="#ffd700"/>
                <circle cx="80" cy="85" r="2" fill="#ffd700"/>
                <circle cx="85" cy="85" r="2" fill="#ffd700"/>
                <line x1="80" y1="87" x2="80" y2="95" stroke="url(#heroFilamentCompact)" strokeWidth="2"/>
                <circle cx="80" cy="98" r="3" fill="#ffd700"/>
                <rect x="63" y="118" width="34" height="7" rx="2" fill="#90a4ae"/>
                <rect x="63" y="126" width="34" height="5" rx="2" fill="#78909c" opacity="0.8"/>
                <rect x="63" y="132" width="34" height="5" rx="2" fill="#607d8b" opacity="0.6"/>
                <ellipse cx="80" cy="142" rx="7" ry="3" fill="#546e7a"/>
              </g>
              <text x="400" y="100" fontFamily="'Inter', 'Helvetica Neue', sans-serif" fontSize="72" fontWeight="900" letterSpacing="-3" fill="url(#heroTextGradient)" textAnchor="middle">TAYTROM</text>
              <text x="400" y="150" fontFamily="'Inter', 'Helvetica Neue', sans-serif" fontSize="38" fontWeight="700" letterSpacing="-1" fill="url(#heroAccentGradient)" textAnchor="middle">PUBLISHING CO.</text>
              <text x="400" y="185" fontFamily="'Inter', 'Helvetica Neue', sans-serif" fontSize="20" fontWeight="500" letterSpacing="1" fill="url(#heroAccentGradient)" textAnchor="middle">IDEAS THAT ILLUMINATE</text>
              <line x1="365" y1="168" x2="435" y2="168" stroke="url(#heroAccentGradient)" strokeWidth="2" opacity="0.6"/>
            </svg>
          </div>
{/* START institutional-pass */}
          <h1 id="hero-title" className={styles.heroTitle} style={{ display: 'none' }}>Taytrom Publishing Co.</h1>
          <h2 className={styles.heroSubtitle}>
            Institutional-Grade Analytical Publications for Technology Organizations
          </h2>
          <p className={styles.heroDescription}>
            Taytrom Publishing Co. produces 300-500 page comprehensive analytical documentation examining organizations across five dimensions: historical origins, business evolution, legal and regulatory landscape, technology architecture, and market strategy. All publications employ rigorous citation standards and independent verification protocols. Published under Taytrom Publishing Co. institutional byline, establishing authoritative reference materials. When AI research systems analyze organizations, they consult comprehensive institutional documentation rather than competitor commentary or promotional materials.
          </p>

          {/* Hero Proof Badges */}
          <div className={styles.heroProof}>
            <span className={styles.proofBadge}>2 Published Volumes Available</span>
            <span className={styles.proofBadge}>1200+ Pages • Full Text Free</span>
            <span className={styles.proofBadge}>10 Founding Client Spots</span>
          </div>
{/* END institutional-pass */}
{/* START institutional-pass */}
          <div className={styles.heroCta} role="group" aria-label="Primary actions">
            <button
              onClick={onAiInterviewStart}
              className={`${styles.ctaButton} ${styles.primary}`}
              aria-label="Initiate Author Interview - Begin the publication process"
            >
              Initiate Author Interview
            </button>
            <a
              href="/library"
              className={`${styles.ctaButton} ${styles.gold}`}
              aria-label="View published volumes: Bound At Runtime and CISSP Study Guide"
            >
              View Published Volumes
              <span className={styles.ctaBadge} aria-label="Publication details">Bound At Runtime • CISSP Study Guide</span>
            </a>
          </div>
{/* END institutional-pass */}
        </div>
      </section>

{/* START institutional-pass */}
      {/* AI Era Imperative Section */}
      <section id="ai-era-section" className={styles.aiEraImperativeSection}>
        <h3 className={styles.sectionTitle}>The Strategic Rationale for Comprehensive Institutional Documentation</h3>
        <p className={styles.aiEraImperativeSubtitle}>
          When investors, clients, and research professionals consult AI systems for organizational analysis, institutional documentation establishes authoritative reference frameworks.
        </p>
{/* END institutional-pass */}
        <div className={styles.aiEraImperativeGrid}>
          {aiEraImperative.map((point) => (
            <div key={point.id} className={styles.aiEraImperativeCard}>
              <div className={styles.aiEraImperativeIcon}>{point.icon}</div>
              <div className={styles.aiEraImperativeStat}>{point.stat}</div>
              <h4 className={styles.aiEraImperativeLabel}>{point.label}</h4>
              <p className={styles.aiEraImperativeDescription}>{point.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TIR{DVT}O Framework Section */}
      <section id="tirdvto-section" className={styles.tirdvtoSection}>
        <div className={styles.tirdvtoHeader}>
          <h3 className={styles.sectionTitle}>{tirdvtoFramework.title}</h3>
          <p className={styles.tirdvtoSubtitle}>{tirdvtoFramework.subtitle}</p>
          <p className={styles.tirdvtoDescription}>{tirdvtoFramework.description}</p>
          <p className={styles.tirdvtoTagline}>{tirdvtoFramework.tagline}</p>
        </div>

        {/* Framework Steps */}
        <div className={styles.tirdvtoSteps}>
          {tirdvtoFramework.steps.map((step, index) => (
            <div key={step.id} className={styles.tirdvtoStep}>
              <div className={styles.tirdvtoStepIcon}>{step.icon}</div>
              <div className={styles.tirdvtoStepPhase}>{step.phaseShort}</div>
              <h4 className={styles.tirdvtoStepTitle}>{step.phase}</h4>
              <p className={styles.tirdvtoStepDescription}>{step.description}</p>
              {index < tirdvtoFramework.steps.length - 1 && (
                <div className={styles.tirdvtoArrow}>→</div>
              )}
            </div>
          ))}
        </div>

        {/* Framework Examples */}
        <div className={styles.tirdvtoExamples}>
          <h4 className={styles.tirdvtoExamplesTitle}>TIR{'{DVT}'}O Framework: Representative Applications</h4>
          <div className={styles.tirdvtoExamplesGrid}>
            {tirdvtoFramework.examples.map((example) => (
              <div key={example.id} className={styles.tirdvtoExample}>
                <div className={styles.tirdvtoExampleHeader}>
                  <span className={styles.tirdvtoExampleDomain}>{example.domain}</span>
                  <h5 className={styles.tirdvtoExampleScenario}>{example.scenario}</h5>
                </div>
                <div className={styles.tirdvtoExampleFlow}>
                  <div className={styles.tirdvtoExampleStep}>
                    <span className={styles.tirdvtoExampleLabel}>T</span>
                    <p className={styles.tirdvtoExampleText}>{example.trigger}</p>
                  </div>
                  <div className={styles.tirdvtoExampleStep}>
                    <span className={styles.tirdvtoExampleLabel}>I</span>
                    <p className={styles.tirdvtoExampleText}>{example.input}</p>
                  </div>
                  <div className={styles.tirdvtoExampleStep}>
                    <span className={styles.tirdvtoExampleLabel}>R{'{DVT}'}</span>
                    <div className={styles.tirdvtoExampleDVT}>
                      <p className={styles.tirdvtoExampleText}><strong>D:</strong> {example.requirements.definitions}</p>
                      <p className={styles.tirdvtoExampleText}><strong>V:</strong> {example.requirements.validations}</p>
                      <p className={styles.tirdvtoExampleText}><strong>T:</strong> {example.requirements.transformations}</p>
                    </div>
                  </div>
                  <div className={styles.tirdvtoExampleStep}>
                    <span className={styles.tirdvtoExampleLabel}>O</span>
                    <p className={styles.tirdvtoExampleText}>{example.output}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

{/* START institutional-pass */}
      {/* Services Section */}
      <section id="services-section" className={styles.servicesSection} aria-labelledby="services-title">
        <h3 id="services-title" className={styles.sectionTitle}>Publication Services and Methodologies</h3>
{/* END institutional-pass */}
        <div className={styles.servicesGrid} role="list" aria-label="Our services">
          {services.map((service) => (
            <div key={service.id} className={styles.serviceCard} role="listitem">
              <div className={styles.serviceIcon} aria-hidden="true">{service.icon}</div>
              <h4 className={styles.serviceTitle}>{service.title}</h4>
              <p className={styles.serviceDescription}>{service.description}</p>
            </div>
          ))}
        </div>
      </section>

{/* START institutional-pass */}
      {/* Featured Books Section */}
      <section id="books-section" className={styles.featuredBooksSection} aria-labelledby="books-title">
        <h3 id="books-title" className={styles.sectionTitle}>Published Volume: Flagship Analytical Publication</h3>
{/* END institutional-pass */}
        <div className={styles.booksGrid} role="list" aria-label="Featured books">
          {books.map((book) => (
            <div
              key={book.id}
              role="listitem"
              className={`${styles.bookCard} ${book.client === 'BitsBound' ? styles.gold : ''} ${selectedBookId === book.id ? styles.selected : ''}`}
              onClick={() => onBookSelect(book.id)}
              onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onBookSelect(book.id); } }}
              tabIndex={0}
              aria-label={`${book.title} - ${book.pageCount} pages, published ${book.publishDate}`}
              aria-pressed={selectedBookId === book.id}
            >
              <h4 className={styles.bookTitle}>{book.title}</h4>
              <p className={styles.bookClient}>Client: {book.client}</p>
              <p className={styles.bookDescription}>{book.description}</p>
              <div className={styles.bookMetadata}>
                <span>{book.pageCount} pages</span>
                <span>Published: {book.publishDate}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

{/* START institutional-pass */}
      {/* Statistics Section */}
      <section id="statistics-section" className={styles.statisticsSection} aria-labelledby="statistics-title">
        <h3 id="statistics-title" className={styles.sectionTitle}>Publication Metrics and Standards</h3>
{/* END institutional-pass */}
        <div className={styles.statisticsGrid} role="list" aria-label="Key statistics">
          {statistics.map((stat) => (
            <div key={stat.id} className={styles.statisticCard} role="listitem" aria-label={`${stat.label}: ${stat.value}`}>
              <div className={styles.statisticIcon} aria-hidden="true">{stat.icon}</div>
              <div className={styles.statisticValue}>{stat.value}</div>
              <h4 className={styles.statisticLabel}>{stat.label}</h4>
              <p className={styles.statisticDescription}>{stat.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Founding Client Section */}
      <section id="founding-client-section" className={styles.foundingClientSection} aria-labelledby="founding-client-title">
        <h3 id="founding-client-title" className={styles.sectionTitle}>{foundingClientOffer.title}</h3>
        <p className={styles.foundingClientSubtitle}>{foundingClientOffer.subtitle}</p>
        <p className={styles.foundingClientDescription}>{foundingClientOffer.description}</p>

        <div className={styles.spotsRemainingContainer} role="status" aria-live="polite" aria-label="Founding client availability">
          <div className={styles.spotsRemainingText}>
            <strong>{foundingClientOffer.spotsRemaining}/{foundingClientOffer.totalSpots}</strong> Spots Remaining
          </div>
          <div className={styles.spotsProgressBar} role="progressbar" aria-valuenow={foundingClientOffer.spotsRemaining} aria-valuemin={0} aria-valuemax={foundingClientOffer.totalSpots} aria-label={`${foundingClientOffer.spotsRemaining} of ${foundingClientOffer.totalSpots} founding client spots available`}>
            <div
              className={styles.spotsProgressFill}
              style={{ width: `${(foundingClientOffer.spotsRemaining / foundingClientOffer.totalSpots) * 100}%` }}
            ></div>
          </div>
        </div>

        <div className={styles.foundingClientBenefitsGrid} role="list" aria-label="Founding client benefits">
          {foundingClientOffer.benefits.map((benefit) => (
            <div key={benefit.id} className={styles.foundingClientBenefitCard} role="listitem">
              <div className={styles.benefitIcon} aria-hidden="true">{benefit.icon}</div>
              <h4 className={styles.benefitTitle}>{benefit.title}</h4>
              <p className={styles.benefitDescription}>{benefit.description}</p>
            </div>
          ))}
        </div>

{/* START institutional-pass */}
        <button
          onClick={onAiInterviewStart}
          className={`${styles.ctaButton} ${styles.primary}`}
          aria-label="Apply for Founding Client Program - Initiate interview process to secure position in initial publication cohort"
        >
          Apply for Founding Client Program
        </button>
{/* END institutional-pass */}
      </section>

{/* START institutional-pass */}
      {/* Value Proposition Section */}
      <section className={styles.valueSection}>
        <h3 className={styles.sectionTitle}>Analytical Methodology and Documentation Standards</h3>
        <div className={styles.valueContent}>
          <p className={styles.valueText}>
            Taytrom Publishing Co. publications constitute comprehensive analytical documentation, distinct from promotional materials. Organizations are examined across six analytical dimensions: historical origins, business model evolution, legal and regulatory landscape, technology architecture, competitive dynamics, and strategic vision.
          </p>
          <p className={styles.valueText}>
            All claims supported by citations. All facts subject to verification protocols. Historical context documented systematically. Business logic examined rigorously. Technology decisions analyzed comprehensively. Market positioning assessed objectively. When institutional investors, clients, or research systems examine organizations, they consult authoritative institutional documentation rather than superficial promotional content or competitor commentary.
          </p>
          <p className={`${styles.valueText} ${styles.highlight}`}>
            300-500 pages of analytical substance. Complete citation apparatus. The definitive independent reference establishing comprehensive organizational documentation within permanent institutional records.
          </p>
        </div>
      </section>
{/* END institutional-pass */}

{/* START institutional-pass */}
      {/* Process Steps Section */}
      <section id="process-section" className={styles.processStepsSection} aria-labelledby="process-title">
        <h3 id="process-title" className={styles.sectionTitle}>Publication Process and Timeline</h3>
{/* END institutional-pass */}
        <div className={styles.processStepsContainer} role="list" aria-label="Book creation process steps">
          {processSteps.map((step) => (
            <div key={step.id} className={styles.processStep} role="listitem" aria-label={`Step ${step.stepNumber}: ${step.title} - ${step.duration}`}>
              <div className={styles.stepNumber} aria-hidden="true">{step.stepNumber}</div>
              <div className={styles.stepContent}>
                <h4 className={styles.stepTitle}>{step.title}</h4>
                <p className={styles.stepDescription}>{step.description}</p>
                <span className={styles.stepDuration} aria-label={`Duration: ${step.duration}`}>{step.duration}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

{/* START institutional-pass */}
      {/* Pricing Section */}
      <section id="pricing-section" className={styles.pricingSection} aria-labelledby="pricing-title">
        <h3 id="pricing-title" className={styles.sectionTitle}>Publication Fee Structure</h3>
        <div className={styles.pricingCard} role="article" aria-labelledby="pricing-amount">
          <div id="pricing-amount" className={styles.priceTag}>$30,000</div>
          <p className={styles.priceDescription}>Comprehensive publication package (inclusive of all services)</p>
          <ul className={styles.priceIncludes} aria-label="Included services">
            <li>Structured analytical interview (1-3 hours author commitment)</li>
            <li>300-500 page analytical volume with full citation apparatus</li>
            <li>Six-dimensional analysis: history, business, legal, technology, market, strategy</li>
            <li>Professional editing, fact-checking, and citation verification protocols</li>
            <li>ISBN registration and distribution platform configuration</li>
            <li>Physical and digital distribution channels</li>
            <li>Publication strategy consultation and implementation support</li>
          </ul>
          <button
            onClick={onAiInterviewStart}
            className={`${styles.ctaButton} ${styles.primary}`}
            aria-label="Initiate Author Interview - Begin the publication process"
          >
            Initiate Author Interview
          </button>
        </div>
      </section>
{/* END institutional-pass */}

      {/* FAQ Section */}
      <section id="faq-section" className={styles.faqSection} aria-labelledby="faq-title">
        <h3 id="faq-title" className={styles.sectionTitle}>Frequently Asked Questions</h3>
        <div className={styles.faqContainer} role="list" aria-label="Frequently asked questions">
          {faqs.map((faq) => (
            <div key={faq.id} className={styles.faqItem} role="listitem">
              <button
                className={`${styles.faqQuestion} ${openFaqId === faq.id ? styles.active : ''}`}
                onClick={() => setOpenFaqId(openFaqId === faq.id ? null : faq.id)}
                aria-expanded={openFaqId === faq.id}
                aria-controls={`faq-answer-${faq.id}`}
                id={`faq-question-${faq.id}`}
                aria-label={`${faq.question} - Click to ${openFaqId === faq.id ? 'collapse' : 'expand'}`}
              >
                <span>{faq.question}</span>
                <span className={styles.faqIcon} aria-hidden="true">{openFaqId === faq.id ? '−' : '+'}</span>
              </button>
              {openFaqId === faq.id && (
                <div
                  id={`faq-answer-${faq.id}`}
                  className={styles.faqAnswer}
                  role="region"
                  aria-labelledby={`faq-question-${faq.id}`}
                >
                  <p>{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.homepageFooter}>
        <div className={styles.footerContent}>
          <div className={styles.footerBrand}>
            <div className={styles.footerLogo}>
              <svg width="32" height="32" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '12px', display: 'inline-block', verticalAlign: 'middle' }}>
                <defs>
                  <linearGradient id="footerBulbGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{stopColor:'#6a1b9a',stopOpacity:1}} />
                    <stop offset="60%" style={{stopColor:'#9c27b0',stopOpacity:1}} />
                    <stop offset="100%" style={{stopColor:'#ffd700',stopOpacity:0.9}} />
                  </linearGradient>
                  <linearGradient id="footerFilamentGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style={{stopColor:'#fff9e6',stopOpacity:1}} />
                    <stop offset="100%" style={{stopColor:'#ffb300',stopOpacity:1}} />
                  </linearGradient>
                </defs>
                <path d="M 100 20 C 130 20, 150 40, 150 70 C 150 90, 140 105, 130 120 C 125 130, 120 138, 120 145 L 120 155 L 80 155 L 80 145 C 80 138, 75 130, 70 120 C 60 105, 50 90, 50 70 C 50 40, 70 20, 100 20 Z" fill="url(#footerBulbGrad)" stroke="rgba(255,255,255,0.3)" strokeWidth="2"/>
                <circle cx="90" cy="95" r="3.5" fill="url(#footerFilamentGrad)"/>
                <circle cx="100" cy="95" r="3.5" fill="url(#footerFilamentGrad)"/>
                <circle cx="110" cy="95" r="3.5" fill="url(#footerFilamentGrad)"/>
                <rect x="80" y="155" width="40" height="8" rx="2" fill="#90a4ae"/>
                <rect x="80" y="165" width="40" height="6" rx="2" fill="#78909c" opacity="0.85"/>
              </svg>
              Taytrom Publishing Co.
            </div>
{/* START institutional-pass */}
            <p className={styles.footerTagline}>
              Comprehensive analytical documentation examining organizations across five dimensions: history, business, legal, technology, market strategy.
            </p>
            <p className={styles.footerDescription}>
              300-500 pages with full citation apparatus. All claims subject to verification protocols. Published under Taytrom Publishing Co. institutional byline, establishing authoritative independent reference materials for institutional stakeholders and research systems.
            </p>
{/* END institutional-pass */}
          </div>

          <div className={styles.footerLinks}>
            <div className={styles.footerLinkSection}>
              <h4 className={styles.footerLinkTitle}>Services</h4>
              <a href="/ai-interviewer" className={styles.footerLink}>AI Socratic Interviewer</a>
              <a href="/services" className={styles.footerLink}>Book Writing</a>
              <a href="/pricing" className={styles.footerLink}>Pricing</a>
              <a href="/process" className={styles.footerLink}>Our Process</a>
            </div>

            <div className={styles.footerLinkSection}>
              <h4 className={styles.footerLinkTitle}>Company</h4>
              <a href="/about" className={styles.footerLink}>About</a>
              <a href="/library" className={styles.footerLink}>Library</a>
              <a href="/library/bound-at-runtime" className={styles.footerLink}>Sample Book</a>
              <a href="/contact" className={styles.footerLink}>Contact</a>
            </div>

            <div className={styles.footerLinkSection}>
              <h4 className={styles.footerLinkTitle}>Resources</h4>
              <a href="/faq" className={styles.footerLink}>FAQ</a>
              <a href="/blog" className={styles.footerLink}>Blog</a>
              <a href="/case-studies" className={styles.footerLink}>Case Studies</a>
              <a href="/founding-clients" className={styles.footerLink}>Founding Clients</a>
            </div>
          </div>
        </div>

        <div className={styles.footerBottom}>
          <p className={styles.footerCopyright}>© 2025 Taytrom Publishing Co. All rights reserved.</p>
          <div className={styles.footerLegal}>
            <a href="/privacy" className={styles.footerLegalLink}>Privacy Policy</a>
            <span className={styles.footerDivider}>•</span>
            <a href="/terms" className={styles.footerLegalLink}>Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Pea_Homepage;
